import { Alert, Group, Loader, NumberInput, SimpleGrid, Textarea, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconPlus } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import Button from 'components/Button';
import FormInputGroup from 'components/forms/FormInputGroup';
import FormWrapper from 'components/forms/FormWrapper';
import OrganizationStructureScope from 'components/forms/OrganizationStructureScope';
import { useDirtyFormHandler } from 'hooks/use-dirty-form-handler';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { ROLE_ADMIN_ID } from 'model/Role';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { DASHBOARD_PAGE_PATH, HR_FILE_CATEGORIES_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

/** Dedicated create/edit page for one HR file category. */
export default function HrFileCategoryForm() {
  const { getAction, roleId } = useApi();
  const { isTablet } = useResponsiveDesign();
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const editing = categoryId !== undefined;
  const [version, setVersion] = useState<number | null>(null);
  const [loading, setLoading] = useState(editing);
  const [reloadToken, setReloadToken] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const title = `${editing ? 'Upraviť' : 'Pridať'} kategóriu HR súborov`;

  const form = useForm({
    initialValues: {
      isGlobal: true,
      organizationIds: [] as string[],
      name: '',
      description: '',
      displayOrder: 0,
    },
    validate: {
      name: (value) => (value.trim() ? null : 'Pole je povinné'),
      organizationIds: (value, values) =>
        !values.isGlobal && value.length === 0 ? 'Vyberte aspoň jednu organizáciu' : null,
    },
  });
  useDirtyFormHandler(form, true);

  useEffect(() => {
    form.reset();
    if (!categoryId) {
      setVersion(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setVersion(null);
    getAction('HrListFileCategories')({ query: { filters: { includeArchived: true } } })
      .then((categories) => {
        const category = categories.find((candidate) => String(candidate.categoryId) === categoryId);
        if (!category) {
          setError('Konfigurácia neexistuje alebo k nej nemáte prístup.');
          return;
        }
        setVersion(category.version ?? null);
        const authoritativeValues = {
          isGlobal: category.isGlobal,
          organizationIds: category.organizations.map((organization) => String(organization.organizationId)),
          name: category.name ?? '',
          description: category.description ?? '',
          displayOrder: category.displayOrder ?? 0,
        };
        form.setValues(authoritativeValues);
        form.resetDirty(authoritativeValues);
      })
      .catch(() => setError('Konfiguráciu sa nepodarilo načítať.'))
      .finally(() => setLoading(false));
  }, [categoryId, getAction, reloadToken]);

  const submit = form.onSubmit(async (values) => {
    setSaving(true);
    setError(null);
    const payload = {
      isGlobal: values.isGlobal,
      organizationIds: values.isGlobal ? [] : values.organizationIds.map(Number),
      name: values.name.trim(),
      description: values.description.trim() || undefined,
      displayOrder: values.displayOrder,
    };

    try {
      if (editing) {
        await getAction('HrUpdateFileCategory')({
          parameters: { categoryId: categoryId! },
          payload: { ...payload, version: version! },
        });
      } else {
        await getAction('HrCreateFileCategory')({ payload });
      }
      notifications.show({
        title: 'Kategória HR súborov bola uložená',
        message: 'Zmena je dostupná v zozname.',
        color: SUCCESS_NOTIFICATION_COLOR,
      });
      form.resetDirty(values);
      navigate(HR_FILE_CATEGORIES_PAGE_PATH.original);
    } catch (caught) {
      setError('Kategóriu HR súborov sa nepodarilo uložiť. Skontrolujte rozsah, jedinečnosť a povinné hodnoty.');
    } finally {
      setSaving(false);
    }
  });

  if (roleId !== ROLE_ADMIN_ID) return <Navigate to={DASHBOARD_PAGE_PATH.original} replace />;
  if (loading) {
    return (
      <DashboardLayout
        title={title}
        breadcrumbs={[{ title: 'Kategórie HR súborov', link: HR_FILE_CATEGORIES_PAGE_PATH.original }, { title }]}
      >
        <Group position="center" py={80}>
          <Loader aria-label="Načítavam kategóriu HR súborov" />
        </Group>
      </DashboardLayout>
    );
  }
  if (editing && version === null) {
    return (
      <DashboardLayout
        title={title}
        breadcrumbs={[{ title: 'Kategórie HR súborov', link: HR_FILE_CATEGORIES_PAGE_PATH.original }, { title }]}
      >
        <Alert color="red" title="Kategóriu HR súborov nie je možné upraviť">
          {error ?? 'Kategóriu HR súborov sa nepodarilo načítať.'}
          <Button mt={12} variant="tertiary" onClick={() => setReloadToken((value) => value + 1)}>
            Načítať znova
          </Button>
        </Alert>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={title}
      breadcrumbs={[{ title: 'Kategórie HR súborov', link: HR_FILE_CATEGORIES_PAGE_PATH.original }, { title }]}
    >
      {error && (
        <Alert color="red" mb={16}>
          {error}
        </Alert>
      )}
      <FormWrapper
        primaryButtonText={editing ? 'Uložiť' : 'Vytvoriť'}
        primaryIcon={editing ? <IconCheck stroke={1.5} /> : <IconPlus stroke={1.5} />}
        secondaryButtonText="Zrušiť"
        onSecondaryClick={() => navigate(HR_FILE_CATEGORIES_PAGE_PATH.original)}
        skipSecondaryButtonConfirm={!form.isDirty()}
        onSubmit={submit}
        loading={saving}
      >
        <FormInputGroup groupTitle="Základné info">
          <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
            <TextInput size="lg" label="Názov" withAsterisk {...form.getInputProps('name')} />
            <Textarea size="lg" label="Popis" {...form.getInputProps('description')} />
          </SimpleGrid>
        </FormInputGroup>
        <OrganizationStructureScope
          allOrganizations={form.values.isGlobal}
          selectedOrganizations={form.values.organizationIds}
          allOrganizationsText="Kategória súborov je dostupná pre všetky organizácie."
          error={form.errors.organizationIds}
          name="organizationIds"
          showDiscarded
          onAllOrganizationsChange={(value) => form.setFieldValue('isGlobal', value)}
          onSelectedOrganizationsChange={(value) => form.setFieldValue('organizationIds', value)}
        />
        <FormInputGroup groupTitle="Nastavenia">
          <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
            <NumberInput size="lg" label="Poradie zobrazenia" min={0} {...form.getInputProps('displayOrder')} />
          </SimpleGrid>
        </FormInputGroup>
      </FormWrapper>
    </DashboardLayout>
  );
}
